import {
  createContext,
  useContext,
  useReducer,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import { usePersistedState } from '../hooks/usePersistedState';
import { LEGACY_TAB_FALLBACK } from '../types';

/** Layout state managed by LayoutContext. */
export interface LayoutState {
  sidebarOpen: boolean;
  sidebarWidth: number;
  sidebarTab: string;
  inspectorOpen: boolean;
  quickAccessOpen: boolean;
  toolOptionsVisible: boolean;
}

/** Actions dispatched to the layout reducer. */
export type LayoutAction =
  | { type: 'layout:toggleSidebar' }
  | { type: 'layout:setSidebarTab'; tab: string }
  | { type: 'layout:setSidebarWidth'; width: number }
  | { type: 'layout:toggleInspector' }
  | { type: 'layout:toggleQuickAccess' }
  | { type: 'layout:toggleToolOptions' }
  | { type: 'layout:setToolOptions'; visible: boolean };

/** Value provided by LayoutContext. */
export interface LayoutContextValue {
  /** Current layout state */
  state: LayoutState;
  /** Dispatch layout actions */
  dispatch: React.Dispatch<LayoutAction>;
}

/** Default initial layout state. */
export const INITIAL_LAYOUT: LayoutState = {
  sidebarOpen: true,
  sidebarWidth: 320,
  sidebarTab: 'map',
  inspectorOpen: false,
  quickAccessOpen: false,
  toolOptionsVisible: true,
};

/** Clamp a value between min and max. */
function clampWidth(width: number): number {
  return Math.min(600, Math.max(200, width));
}

/** Reducer for layout state transitions. */
export function layoutReducer(state: LayoutState, action: LayoutAction): LayoutState {
  switch (action.type) {
    case 'layout:toggleSidebar':
      return { ...state, sidebarOpen: !state.sidebarOpen };
    case 'layout:setSidebarTab': {
      const resolvedTab = LEGACY_TAB_FALLBACK[action.tab] ?? action.tab;
      return { ...state, sidebarTab: resolvedTab };
    }
    case 'layout:setSidebarWidth':
      return { ...state, sidebarWidth: clampWidth(action.width) };
    case 'layout:toggleInspector':
      return { ...state, inspectorOpen: !state.inspectorOpen };
    case 'layout:toggleQuickAccess':
      return { ...state, quickAccessOpen: !state.quickAccessOpen };
    case 'layout:toggleToolOptions':
      return { ...state, toolOptionsVisible: !state.toolOptionsVisible };
    case 'layout:setToolOptions':
      return { ...state, toolOptionsVisible: action.visible };
    default:
      return state;
  }
}

const LayoutContext = createContext<LayoutContextValue | null>(null);

/** Apply legacy tab fallback to persisted layout state. */
function applyTabFallback(state: LayoutState): LayoutState {
  const resolved = LEGACY_TAB_FALLBACK[state.sidebarTab];
  if (resolved) {
    return { ...state, sidebarTab: resolved };
  }
  return state;
}

/** Provider for layout state. Place above WorkspaceProvider in the tree. */
export function LayoutProvider({ children }: { children: ReactNode }) {
  const [persisted, setPersisted] = usePersistedState<LayoutState>('layout', INITIAL_LAYOUT);

  const [state, dispatch] = useReducer(layoutReducer, applyTabFallback(persisted));

  // Persist state changes
  useEffect(() => {
    setPersisted(state);
  }, [state, setPersisted]);

  // Sync CSS custom properties
  useEffect(() => {
    const style = document.documentElement.style;
    style.setProperty('--sidebar-width', `${state.sidebarWidth}px`);
    // --toolbar-height removed (043-floating-toolbar: horizontal toolbar replaced by floating panel)
    style.setProperty('--sidebar-collapsed', state.sidebarOpen ? '0' : '1');
  }, [state.sidebarWidth, state.sidebarOpen]);

  const value = useMemo<LayoutContextValue>(() => ({
    state,
    dispatch,
  }), [state, dispatch]);

  return (
    <LayoutContext.Provider value={value}>{children}</LayoutContext.Provider>
  );
}

/**
 * Access LayoutContext value. Must be used within a LayoutProvider.
 * @throws Error if used outside LayoutProvider.
 */
export function useLayoutContext(): LayoutContextValue {
  const ctx = useContext(LayoutContext);
  if (!ctx) throw new Error('useLayoutContext must be used within LayoutProvider');
  return ctx;
}
